/**
 * Shared Day.js helpers for facility-timezone dates and UTC (Zulu) output.
 * Load after `snippets/dayjs-scripts.liquid` (dayjs + utc + timezone + customParseFormat).
 */
/** Extend dayjs plugins (safe to call repeatedly — dayjs.min.js may load more than once with defer). */
function ensureA9DayjsPlugins() {
  if (typeof dayjs === 'undefined') return false;
  if (typeof dayjs_plugin_customParseFormat !== 'undefined') dayjs.extend(dayjs_plugin_customParseFormat);
  if (typeof dayjs_plugin_utc !== 'undefined') dayjs.extend(dayjs_plugin_utc);
  if (typeof dayjs_plugin_timezone !== 'undefined') dayjs.extend(dayjs_plugin_timezone);
  const ok = typeof dayjs.tz === 'function';
  if (typeof window !== 'undefined') window.__a9DayjsPlugins = ok;
  return ok;
}

if (typeof window !== 'undefined') {
  window.ensureA9DayjsPlugins = ensureA9DayjsPlugins;
  ensureA9DayjsPlugins();
}

(function () {
  'use strict';

  if (typeof window !== 'undefined' && window.A9DateUtils) return;

  function hasTz() {
    ensureA9DayjsPlugins();
    return typeof dayjs !== 'undefined' && typeof dayjs.tz === 'function';
  }

  function zone(tz) {
    return tz ? String(tz).trim() : '';
  }

  /** API contract: ISO-8601 Zulu without milliseconds. */
  function toZuluMs(ms) {
    return dayjs(ms).toISOString().replace(/\.\d{3}Z$/, 'Z');
  }

  function toZuluFromDayjs(d) {
    if (!d || !d.isValid()) return null;
    return d.utc().format('YYYY-MM-DDTHH:mm:ss') + 'Z';
  }

  function fmtOffsetFromMinutes(offsetMin) {
    const sign = offsetMin >= 0 ? '+' : '-';
    const h = Math.floor(Math.abs(offsetMin) / 60);
    const mn = Math.abs(offsetMin) % 60;
    return `${sign}${String(h).padStart(2, '0')}:${String(mn).padStart(2, '0')}`;
  }

  function nowInZone(tz) {
    const z = zone(tz);
    if (hasTz() && z) {
      const d = dayjs().tz(z);
      if (d.isValid()) return d;
    }
    return dayjs();
  }

  function currentMdy(tz) {
    return nowInZone(tz).format('M/D/YYYY');
  }

  function currentYmd(tz) {
    return nowInZone(tz).format('YYYY-MM-DD');
  }

  function currentTime12h(tz) {
    return nowInZone(tz).format('h:mm A');
  }

  /**
   * Parse `M/D/YYYY` (or `MM/DD/YYYY`) as **midnight of that calendar day** in the facility zone.
   *
   * NOTE: `dayjs.tz(s, format, zone)` parses `s` in **browser** TZ first and then
   * converts to `zone`, which can shift the day by ±1 for shoppers outside the
   * facility timezone. We build the date string explicitly so the parse is anchored
   * to the facility zone from the start.
   */
  function parseMdy(mdy, tz) {
    const z = zone(tz);
    const s = String(mdy || '').trim();
    if (!s) return null;
    const parts = s.split('/');
    if (parts.length !== 3) return null;
    const m = parseInt(parts[0], 10);
    const dNum = parseInt(parts[1], 10);
    const y = parseInt(parts[2], 10);
    if (!m || !dNum || !y) return null;
    const iso = `${y}-${String(m).padStart(2, '0')}-${String(dNum).padStart(2, '0')} 00:00:00`;
    if (hasTz() && z) {
      const d = dayjs.tz(iso, 'YYYY-MM-DD HH:mm:ss', z);
      if (d.isValid()) return d;
    }
    return dayjs(`${y}-${String(m).padStart(2, '0')}-${String(dNum).padStart(2, '0')}`);
  }

  function mdyToYmd(mdy, tz) {
    const d = parseMdy(mdy, tz);
    return d && d.isValid() ? d.format('YYYY-MM-DD') : null;
  }

  function ymdToMdy(ymd, tz) {
    const z = zone(tz);
    const y = String(ymd || '').trim();
    if (!y) return null;
    if (hasTz() && z) {
      const d = dayjs.tz(y + ' 00:00:00', 'YYYY-MM-DD HH:mm:ss', z);
      if (d.isValid()) return d.format('M/D/YYYY');
    }
    return y;
  }

  function parseTime12h(timeStr) {
    const m = String(timeStr || '').trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!m) return null;
    let h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    const ap = m[3].toUpperCase();
    if (h < 1 || h > 12 || min < 0 || min > 59) return null;
    if (ap === 'PM' && h !== 12) h += 12;
    if (ap === 'AM' && h === 12) h = 0;
    return { hours24: h, minutes: min };
  }

  function time12hToMinutes(timeStr) {
    const t = parseTime12h(timeStr);
    if (!t) return NaN;
    return t.hours24 * 60 + t.minutes;
  }

  /** Minutes since midnight → "hh:mm A" label (wraps across midnight). */
  function minutesToTime12h(totalMinutes) {
    if (!Number.isFinite(totalMinutes)) return null;
    const minutes = ((Math.round(totalMinutes) % 1440) + 1440) % 1440;
    ensureA9DayjsPlugins();
    if (typeof dayjs !== 'undefined' && typeof dayjs.utc === 'function') {
      return dayjs.utc(minutes * 60000).format('hh:mm A');
    }
    const h = Math.floor(minutes / 60);
    const period = h >= 12 ? 'PM' : 'AM';
    return `${String(h % 12 || 12).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')} ${period}`;
  }

  /**
   * Wall clock on calendar day YYYY-MM-DD in facility TZ → UTC range for a booking.
   * @returns {{ startUtcMs, endUtcMs, startOffset, endOffset }} or null
   */
  function localSlotToUtc(ymd, hours24, minutes, durationHours, tz) {
    const z = zone(tz);
    if (!hasTz() || !z || !ymd) return null;
    const ds = `${ymd} ${String(hours24).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
    const start = dayjs.tz(ds, 'YYYY-MM-DD HH:mm:ss', z);
    if (!start.isValid()) return null;
    const end = start.add(durationHours, 'hour');
    if (!end.isValid()) return null;
    return {
      startUtcMs: start.valueOf(),
      endUtcMs: end.valueOf(),
      startOffset: start.utcOffset(),
      endOffset: end.utcOffset()
    };
  }

  /**
   * True when a wall-clock time does not exist on that day because of the spring-forward
   * DST jump (e.g. America/New_York skips 02:00–02:59). A skipped time normalizes to a
   * different wall time, so the parsed result differs from the requested hour/minute.
   * @param {string} ymd      calendar day as YYYY-MM-DD
   * @param {number} hours24  start hour 0–23
   * @param {number} minutes  start minute 0–59
   * @param {string} tz       facility IANA timezone
   */
  function isWallTimeSkippedByDst(ymd, hours24, minutes, tz) {
    const z = zone(tz);
    const y = String(ymd || '').trim();
    if (!hasTz() || !z || !y) return false;
    const h = Number(hours24);
    const m = Number(minutes);
    if (!Number.isInteger(h) || !Number.isInteger(m)) return false;
    const wall = `${y} ${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`;
    const parsed = dayjs.tz(wall, 'YYYY-MM-DD HH:mm:ss', z);
    if (!parsed.isValid()) return false;
    return parsed.hour() !== h || parsed.minute() !== m;
  }

  /** Unparseable input answers true so the caller still checks each slot. */
  function hasDstTransitionOnDay(ymd, tz) {
    const z = zone(tz);
    const y = String(ymd || '').trim();
    if (!hasTz() || !z || !y) return true;
    const dayStart = dayjs.tz(`${y} 00:00:00`, 'YYYY-MM-DD HH:mm:ss', z);
    if (!dayStart.isValid()) return true;
    if (dayStart.utcOffset() !== dayStart.add(1, 'day').startOf('day').utcOffset()) return true;
    // Spring-forward at 00:00 (Havana, Santiago, Beirut): no midnight, so the offset check is a false negative.
    return isWallTimeSkippedByDst(y, 0, 0, z);
  }

  function localMdyTimeToDayjs(mdy, time12h, tz) {
    const ymd = mdyToYmd(mdy, tz);
    const t = parseTime12h(time12h);
    const z = zone(tz);
    if (!ymd || !t || !hasTz() || !z) return null;
    const start = dayjs.tz(
      `${ymd} ${String(t.hours24).padStart(2, '0')}:${String(t.minutes).padStart(2, '0')}:00`,
      'YYYY-MM-DD HH:mm:ss',
      z
    );
    return start.isValid() ? start : null;
  }

  function hoursUntilMdyTime(mdy, time12h, tz) {
    const slot = localMdyTimeToDayjs(mdy, time12h, tz);
    if (!slot) return NaN;
    return slot.diff(nowInZone(tz), 'hour', true);
  }

  function isWithinHoursFromNow(mdy, time12h, withinHours, tz) {
    const h = hoursUntilMdyTime(mdy, time12h, tz);
    if (isNaN(h)) return false;
    return h < withinHours && h >= 0;
  }

  /** Calendar day (facility TZ) that is the first day at or after now + hours. */
  function firstCalendarDayAfterHours(hours, tz) {
    return nowInZone(tz).add(hours, 'hour').startOf('day');
  }

  /** YYYY-MM-DD strings for each full day blocked before the day containing now + hours. */
  function blockedCalendarDaysBeforeCutoff(hours, tz) {
    const now = nowInZone(tz);
    const cutoffDay = now.add(hours, 'hour').startOf('day');
    const blocked = [];
    let cursor = now.startOf('day');
    while (cursor.isBefore(cutoffDay)) {
      blocked.push(cursor.format('YYYY-MM-DD'));
      cursor = cursor.add(1, 'day');
      if (blocked.length > 10) break;
    }
    return blocked;
  }

  /** 1 = Monday … 7 = Sunday for MM/D/YYYY in facility TZ. */
  function weekday1to7(mdy, tz) {
    const d = parseMdy(mdy, tz);
    if (!d || !d.isValid()) return null;
    const dow = d.day();
    return dow === 0 ? 7 : dow;
  }

  /**
   * Next calendar YYYY-MM-DD (floating — no timezone).
   * Used so DST fall-back/spring-forward days span 25h/23h in UTC, not fixed 24h.
   */
  function nextCalendarYmd(ymd) {
    const y = String(ymd || '').trim();
    if (!y) return null;
    const d = dayjs(y, 'YYYY-MM-DD', true);
    if (!d.isValid()) return null;
    return d.add(1, 'day').format('YYYY-MM-DD');
  }

  /**
   * Facility-local calendar day → Zulu window for GET /slots.
   * `from` = midnight of ymd in facility TZ; `to` = midnight of the next calendar day.
   * Never uses fixed +24h — fall-back days are 25h, spring-forward days are 23h.
   */
  function dayBoundsZulu(ymd, tz) {
    ensureA9DayjsPlugins();
    const z = zone(tz);
    const y = String(ymd || '').trim();
    if (!z || !y) throw new Error('A9DateUtils.dayBoundsZulu: missing timezone or date');
    if (!hasTz()) throw new Error('A9DateUtils.dayBoundsZulu: dayjs timezone plugin not loaded');
    const dayStart = dayjs.tz(y + ' 00:00:00', 'YYYY-MM-DD HH:mm:ss', z);
    if (!dayStart.isValid()) throw new Error('A9DateUtils.dayBoundsZulu: invalid date or timezone');
    const endYmd = nextCalendarYmd(y);
    if (!endYmd) throw new Error('A9DateUtils.dayBoundsZulu: invalid date');
    const dayEnd = dayjs.tz(endYmd + ' 00:00:00', 'YYYY-MM-DD HH:mm:ss', z);
    if (!dayEnd.isValid()) throw new Error('A9DateUtils.dayBoundsZulu: invalid end date or timezone');
    return {
      from: toZuluFromDayjs(dayStart),
      to: toZuluFromDayjs(dayEnd)
    };
  }

  /** Normalize datepicker / display MDY (strip leading zeros on day only where needed). */
  function normalizeMdy(dateString) {
    if (!dateString || typeof dateString !== 'string') return null;
    const d = parseMdy(dateString, null);
    if (d && d.isValid()) return d.format('M/D/YYYY');
    const m = dateString.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!m) return dateString;
    return `${parseInt(m[1], 10)}/${parseInt(m[2], 10)}/${m[3]}`;
  }

  function parseIso(iso) {
    if (!iso) return null;
    const d = dayjs(iso);
    return d.isValid() ? d : null;
  }

  /** UTC/Zulu API instant → dayjs in facility IANA zone (for league round boundaries). */
  function parseUtcInFacilityZone(iso, tz) {
    if (!iso) return null;
    const z = zone(tz);
    if (hasTz() && z) {
      const d = dayjs.utc(String(iso).trim()).tz(z);
      if (d.isValid()) return d;
    }
    return parseIso(iso);
  }

  function dayjsFromMdy(mdy, tz) {
    return parseMdy(mdy, tz);
  }

  function isMdyAfter(a, b, tz) {
    const da = parseMdy(a, tz);
    const db = parseMdy(b, tz);
    if (!da || !db || !da.isValid() || !db.isValid()) return false;
    return da.isAfter(db, 'day');
  }

  function isMdyBefore(a, b, tz) {
    const da = parseMdy(a, tz);
    const db = parseMdy(b, tz);
    if (!da || !db || !da.isValid() || !db.isValid()) return false;
    return da.isBefore(db, 'day');
  }

  function isMdySameOrAfter(a, b, tz) {
    const da = parseMdy(a, tz);
    const db = parseMdy(b, tz);
    if (!da || !db || !da.isValid() || !db.isValid()) return false;
    return da.isSame(db, 'day') || da.isAfter(db, 'day');
  }

  function isMdySameOrBefore(a, b, tz) {
    const da = parseMdy(a, tz);
    const db = parseMdy(b, tz);
    if (!da || !db || !da.isValid() || !db.isValid()) return false;
    return da.isSame(db, 'day') || da.isBefore(db, 'day');
  }

  /** Datepicker expects MM/DD/YYYY. */
  function toDatepickerMdy(d) {
    if (!d || !d.isValid()) return null;
    return d.format('MM/DD/YYYY');
  }

  function ymdToDatepicker(ymd) {
    const parts = String(ymd || '').trim().split('-');
    if (parts.length !== 3) return ymd;
    return `${parts[1]}/${parts[2]}/${parts[0]}`;
  }

  /** JS Date.getDay(): 0 = Sunday … 6 = Saturday. */
  function dayOfWeek0FromMdy(mdy, tz) {
    const d = parseMdy(mdy, tz);
    return d && d.isValid() ? d.day() : null;
  }

  function dayTypeFromMdy(mdy, tz) {
    const dow = dayOfWeek0FromMdy(mdy, tz);
    if (dow == null) return 'weekday';
    return dow === 0 || dow === 5 || dow === 6 ? 'weekend' : 'weekday';
  }

  /** Block YYYY-MM-DD from range start (inclusive) until today (exclusive), facility TZ. */
  function blockedYmdBeforeTodayInRange(rangeStartIso, todayMdy, tz) {
    const start = leagueInclusiveCalendarDay(rangeStartIso, tz);
    const today = parseMdy(todayMdy, tz);
    if (!start || !today || !start.isValid() || !today.isValid()) return [];
    const blocked = [];
    let cursor = start.startOf('day');
    const todayStart = today.startOf('day');
    while (cursor.isBefore(todayStart)) {
      blocked.push(cursor.format('YYYY-MM-DD'));
      cursor = cursor.add(1, 'day');
      if (blocked.length > 400) break;
    }
    return blocked;
  }

  /** Block each facility-local day from today up to round start day (exclusive). */
  function blockedYmdFromTodayUntil(roundStartIso, todayMdy, tz) {
    const today = parseMdy(todayMdy, tz);
    const roundStart = leagueInclusiveCalendarDay(roundStartIso, tz);
    if (!today || !roundStart || !today.isValid() || !roundStart.isValid()) return [];
    const blocked = [];
    let cursor = today.startOf('day');
    const end = roundStart.startOf('day');
    while (cursor.isBefore(end)) {
      blocked.push(cursor.format('YYYY-MM-DD'));
      cursor = cursor.add(1, 'day');
      if (blocked.length > 400) break;
    }
    return blocked;
  }

  /**
   * Calendar Y/M/D from datepicker value without UTC date-only parsing
   * (`new Date("YYYY-MM-DD")` shifts the day in US timezones).
   */
  function pickerInputToCalendarParts(input) {
    if (input == null || input === '') return null;
    if (input instanceof Date && !isNaN(input.getTime())) {
      return { y: input.getFullYear(), mo: input.getMonth() + 1, d: input.getDate() };
    }
    const s = String(input).trim();
    let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return { y: parseInt(m[1], 10), mo: parseInt(m[2], 10), d: parseInt(m[3], 10) };
    m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (m) return { y: parseInt(m[3], 10), mo: parseInt(m[1], 10), d: parseInt(m[2], 10) };
    return null;
  }

  /** Calendar-day input (MDY / YMD / Date) → long label "May 25, 2026". Timezone-agnostic. */
  function formatMdyLong(mdy) {
    const parts = pickerInputToCalendarParts(mdy);
    if (!parts) return mdy || '';
    const d = dayjs(`${parts.y}-${String(parts.mo).padStart(2, '0')}-${String(parts.d).padStart(2, '0')}`);
    return d.isValid() ? d.format('MMMM D, YYYY') : (mdy || '');
  }

  /** Synthetic datepicker change event from facility-local MDY. */
  function fakePickerEventFromMdy(mdy, tz) {
    const parts = pickerInputToCalendarParts(mdy);
    if (!parts) return { detail: { date: new Date() } };
    return { detail: { date: new Date(parts.y, parts.mo - 1, parts.d) } };
  }

  function mdyFromIsoDatePrefix(iso) {
    if (!iso || typeof iso !== 'string') return null;
    const ymd = iso.split('T')[0];
    const parts = ymd.split('-');
    if (parts.length !== 3) return null;
    return `${parseInt(parts[1], 10)}/${parseInt(parts[2], 10)}/${parts[0]}`;
  }

  function formatLocalFromUtcMs(utcMs, tz, pattern) {
    if (!hasTz() || !zone(tz)) return null;
    const d = dayjs(utcMs).tz(zone(tz));
    return d.isValid() ? d.format(pattern || 'YYYY-MM-DDTHH:mm:ss') : null;
  }

  /** "Sep 9" / "Sep 9–11" / "Sep 30 – Oct 2" in facility TZ. `toUtcMs` is exclusive. */
  function formatUtcRangeLabelShort(fromUtcMs, toUtcMs, tz) {
    const lastUtcMs = toUtcMs - 1;
    const firstMonth = formatLocalFromUtcMs(fromUtcMs, tz, 'MMM');
    const firstDay = formatLocalFromUtcMs(fromUtcMs, tz, 'D');
    const lastMonth = formatLocalFromUtcMs(lastUtcMs, tz, 'MMM');
    const lastDay = formatLocalFromUtcMs(lastUtcMs, tz, 'D');
    if (!firstMonth || !firstDay || !lastMonth || !lastDay) return null;
    if (firstMonth === lastMonth) {
      return firstDay === lastDay ? `${firstMonth} ${firstDay}` : `${firstMonth} ${firstDay}–${lastDay}`;
    }
    return `${firstMonth} ${firstDay} – ${lastMonth} ${lastDay}`;
  }

  /**
   * Inclusive league calendar day from API ISO.
   * Round start/end are calendar days in facility TZ — use the YYYY-MM-DD date part.
   * Parsing the UTC instant can shift the day (e.g. 2026-06-14T00:00:00Z → June 13 in NY).
   */
  function leagueInclusiveCalendarDay(iso, tz) {
    if (iso == null || iso === '') return null;
    const s = String(iso).trim();
    const z = zone(tz);

    const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (ymd && hasTz() && z) {
      const d = dayjs.tz(`${ymd[1]}-${ymd[2]}-${ymd[3]} 00:00:00`, 'YYYY-MM-DD HH:mm:ss', z);
      if (d.isValid()) return d;
    }

    const mdy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (mdy && hasTz() && z) {
      const d = dayjs.tz(
        `${mdy[3]}-${String(mdy[1]).padStart(2, '0')}-${String(mdy[2]).padStart(2, '0')} 00:00:00`,
        'YYYY-MM-DD HH:mm:ss',
        z
      );
      if (d.isValid()) return d;
    }

    const inst = parseUtcInFacilityZone(iso, tz);
    return inst && inst.isValid() ? inst.startOf('day') : null;
  }

  function inclusiveRoundStartDay(startIso, tz) {
    return leagueInclusiveCalendarDay(startIso, tz);
  }

  function inclusiveRoundEndDay(endIso, tz) {
    return leagueInclusiveCalendarDay(endIso, tz);
  }

  /**
   * League datepicker bounds — one reservation per round.
   * Only the next unbought round (`nextRoundIndex` = `league_rounds` metafield value,
   * 0-based into `rounds[]`) is bookable: in progress → today..round end; upcoming →
   * opens on the Friday before its start; no rounds left → fully blocked.
   * @returns {{ leagueBlockedDates: string[], leagueFirstDate: string|null, leagueMaxDate: string|null,
   *   leagueBlock: boolean, roundStartYmd: string|null, roundEndYmd: string|null }}
   */
  function computeLeagueDateConstraints(leagueDates, nextRoundIndex, tz) {
    const out = {
      leagueBlockedDates: [],
      leagueFirstDate: null,
      leagueMaxDate: null,
      leagueBlock: false,
      roundStartYmd: null,
      roundEndYmd: null
    };
    if (!leagueDates || !leagueDates.rounds || !leagueDates.rounds.length) return out;

    const z = zone(tz);
    const now = hasTz() && z ? nowInZone(z) : dayjs();
    const todayMdy = hasTz() && z ? currentMdy(z) : now.format('M/D/YYYY');
    const todayStart = now.startOf('day');
    const rounds = leagueDates.rounds;

    // minDate is always today (set by the datepicker init), so maxDate = today + today disabled
    // leaves nothing selectable.
    const fullyBlocked = () => {
      out.leagueBlock = true;
      out.leagueMaxDate = toDatepickerMdy(todayStart);
      out.leagueBlockedDates = [todayStart.format('YYYY-MM-DD')];
      return out;
    };

    // Rounds are bought in order; skip rounds whose week is already over.
    let roundIndex = nextRoundIndex;
    while (roundIndex < rounds.length) {
      const endDay = inclusiveRoundEndDay(rounds[roundIndex].end, z);
      if (endDay && todayStart.isAfter(endDay, 'day')) {
        roundIndex += 1;
      } else {
        break;
      }
    }

    if (roundIndex >= rounds.length) {
      return fullyBlocked();
    }

    const round = rounds[roundIndex];
    const roundStartDay = inclusiveRoundStartDay(round.start, z);
    const roundEndDay = inclusiveRoundEndDay(round.end, z);
    if (!roundStartDay || !roundEndDay) return out;

    // An upcoming round opens on the Friday before its start day (day(): 5 = Friday).
    if (todayStart.isBefore(roundStartDay, 'day')) {
      const daysSinceFriday = (roundStartDay.day() + 7 - 5) % 7;
      const opensOn = roundStartDay.subtract(daysSinceFriday, 'day');
      if (todayStart.isBefore(opensOn, 'day')) {
        return fullyBlocked();
      }
    }

    out.roundStartYmd = roundStartDay.format('YYYY-MM-DD');
    out.roundEndYmd = roundEndDay.format('YYYY-MM-DD');
    out.leagueMaxDate = toDatepickerMdy(roundEndDay);
    out.leagueFirstDate = todayStart.isBefore(roundStartDay, 'day') ? toDatepickerMdy(roundStartDay) : todayMdy;

    // Disable past days inside the round week and the days between today and the round start.
    out.leagueBlockedDates.push(...blockedYmdBeforeTodayInRange(round.start, todayMdy, z));
    out.leagueBlockedDates.push(...blockedYmdFromTodayUntil(round.start, todayMdy, z));

    // Safety net: every day of the bookable window must stay selectable (TZ edge cases can add them to blocked list).
    let cursor = todayStart.isBefore(roundStartDay, 'day') ? roundStartDay : todayStart;
    while (!cursor.isAfter(roundEndDay, 'day')) {
      const ymd = cursor.format('YYYY-MM-DD');
      out.leagueBlockedDates = out.leagueBlockedDates.filter((d) => d !== ymd);
      cursor = cursor.add(1, 'day');
      if (cursor.diff(todayStart, 'day') > 400) break;
    }

    return out;
  }

  /**
   * Map datepicker output (Date or `MM/DD/YYYY` / `YYYY-MM-DD` string) to calendar day.
   *
   * A picked calendar day is **timezone-agnostic** — we just take the Y/M/D from
   * the click. `parseMdy` + `dayjs.tz` would parse in browser TZ first and then
   * convert to facility TZ, which shifts the day for users west or east of facility.
   * `tz` is kept in the signature for backwards compatibility.
   */
  function datePickerToFacility(pickerInput /* tz unused */) {
    const parts = pickerInputToCalendarParts(pickerInput);
    if (!parts) return null;
    const mdy = `${parts.mo}/${parts.d}/${parts.y}`;
    const ymd = `${parts.y}-${String(parts.mo).padStart(2, '0')}-${String(parts.d).padStart(2, '0')}`;
    const dow = dayjs(ymd).day();
    return {
      mdy,
      ymd,
      dayType: dow === 0 || dow === 5 || dow === 6 ? 'weekend' : 'weekday'
    };
  }

  /** Part of the day for minutes since midnight — the booking popup's four tabs (same split as the app). */
  function partOfDay(minutes) {
    if (!Number.isFinite(minutes) || minutes < 0 || minutes >= 1440) return null;
    if (minutes < 360) return 'overnight';
    if (minutes < 720) return 'morning';
    if (minutes < 1020) return 'afternoon';
    return 'evening';
  }

  /** Names and ranges for partOfDay's keys, in day order — the booking popup tabs and the confirm screen share them. */
  const PART_OF_DAY_LABELS = {
    overnight: { name: 'Overnight', range: '12–6 AM' },
    morning: { name: 'Morning', range: '6 AM–12 PM' },
    afternoon: { name: 'Afternoon', range: '12–5 PM' },
    evening: { name: 'Evening', range: '5 PM–12 AM' }
  };

  /** Today plus the next count−1 facility-local calendar days. Calendar steps, never +24h. */
  function upcomingDays(tz, count) {
    return calendarDays(currentYmd(tz), count);
  }

  /** YYYY-MM-DD ± whole calendar days (date-only, so a DST day is still one step). */
  function addDaysYmd(ymd, count) {
    return dayjs(ymd).add(count, 'day').format('YYYY-MM-DD');
  }

  /** count consecutive calendar days starting at fromYmd, in the shape upcomingDays returns. */
  function calendarDays(fromYmd, count) {
    const first = dayjs(fromYmd);
    const days = [];
    for (let i = 0; i < count; i++) {
      const d = first.add(i, 'day');
      days.push({
        ymd: d.format('YYYY-MM-DD'),
        mdy: d.format('M/D/YYYY'),
        dow: d.day(),
        dayNum: d.date(),
        weekday: d.format('ddd'),
        longLabel: d.format('dddd, MMMM D'),
        // Local-midnight Date: the shape vanillajs-datepicker hands to beforeShowDay.
        pickerDate: new Date(d.year(), d.month(), d.date())
      });
    }
    return days;
  }

  /** Calendar-day input (MDY / YMD) → "Sat, Oct 3". Timezone-agnostic, like formatMdyLong. */
  function formatMdyShort(mdy) {
    const parts = pickerInputToCalendarParts(mdy);
    if (!parts) return mdy || '';
    const d = dayjs(`${parts.y}-${String(parts.mo).padStart(2, '0')}-${String(parts.d).padStart(2, '0')}`);
    return d.isValid() ? d.format('ddd, MMM D') : (mdy || '');
  }

  function formatTimeRange12h(startTime12h, durationHours) {
    const startMinutes = time12hToMinutes(startTime12h);
    if (!Number.isFinite(startMinutes)) return startTime12h || '';
    const dropLeadingZero = (label) => String(label).replace(/^0/, '');
    const end = Number(durationHours) > 0 ? minutesToTime12h(startMinutes + Math.round(Number(durationHours) * 60)) : null;
    return end ? `${dropLeadingZero(startTime12h)} – ${dropLeadingZero(end)}` : dropLeadingZero(startTime12h);
  }

  window.A9DateUtils = {
    hasTz,
    toZuluMs,
    toZuluFromDayjs,
    fmtOffsetFromMinutes,
    nowInZone,
    currentMdy,
    currentYmd,
    currentTime12h,
    parseIso,
    parseUtcInFacilityZone,
    parseMdy,
    mdyToYmd,
    ymdToMdy,
    ymdToDatepicker,
    parseTime12h,
    time12hToMinutes,
    minutesToTime12h,
    localSlotToUtc,
    isWallTimeSkippedByDst,
    hasDstTransitionOnDay,
    localMdyTimeToDayjs,
    hoursUntilMdyTime,
    isWithinHoursFromNow,
    isMdyAfter,
    isMdyBefore,
    isMdySameOrAfter,
    isMdySameOrBefore,
    firstCalendarDayAfterHours,
    blockedCalendarDaysBeforeCutoff,
    weekday1to7,
    dayOfWeek0FromMdy,
    dayTypeFromMdy,
    dayBoundsZulu,
    normalizeMdy,
    pickerInputToCalendarParts,
    formatMdyLong,
    datePickerToFacility,
    fakePickerEventFromMdy,
    mdyFromIsoDatePrefix,
    formatLocalFromUtcMs,
    formatUtcRangeLabelShort,
    computeLeagueDateConstraints,
    partOfDay,
    PART_OF_DAY_LABELS,
    upcomingDays,
    calendarDays,
    addDaysYmd,
    formatMdyShort,
    formatTimeRange12h,
    toDatepickerMdy
  };
})();
